import React, {
  createContext,
  useContext,
  useRef,
  useCallback,
  useMemo,
} from 'react';

type RefreshKey = string;
type RefreshFn = () => void;

export interface RefreshContextValue {
  registerRefresh: (key: RefreshKey, cb: RefreshFn) => () => void; // returns unregister
  refresh: (key: RefreshKey) => void;
  refreshAll: () => void;
  refreshByProvider: (provider: 'aws' | 'gcp' | 'azure' | 'kubernetes') => void;
}

const RefreshContext = createContext<RefreshContextValue | undefined>(
  undefined,
);

export const useRefreshContext = () => {
  const context = useContext(RefreshContext);
  if (!context) {
    throw new Error('useRefreshContext must be used within a RefreshProvider');
  }
  return context;
};

export const RefreshProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const refreshersRef = useRef<Record<RefreshKey, RefreshFn>>({});

  const registerRefresh = useCallback((key: RefreshKey, cb: RefreshFn) => {
    refreshersRef.current[key] = cb;
    return () => {
      if (refreshersRef.current[key] === cb) {
        delete refreshersRef.current[key];
      }
    };
  }, []);

  const refresh = useCallback((key: RefreshKey) => {
    const refreshFn = refreshersRef.current[key];
    if (refreshFn) {
      refreshFn();
    }
  }, []);

  const refreshAll = useCallback(() => {
    Object.values(refreshersRef.current).forEach(fn => fn?.());
  }, []);

  const refreshByProvider = useCallback(
    (provider: 'aws' | 'gcp' | 'azure' | 'kubernetes') => {
      Object.entries(refreshersRef.current).forEach(([key, fn]) => {
        if (key.startsWith(`${provider}:`)) {
          fn?.();
        }
      });
    },
    [],
  );

  const value = useMemo<RefreshContextValue>(
    () => ({
      registerRefresh,
      refresh,
      refreshAll,
      refreshByProvider,
    }),
    [registerRefresh, refresh, refreshAll, refreshByProvider],
  );

  return (
    <RefreshContext.Provider value={value}>{children}</RefreshContext.Provider>
  );
};
